import { Fragment, Suspense, lazy, useEffect, useState } from 'react';
// only the language list: importing the package root would put the question bank in the first download
import { LANGS, type Lang } from '@chemai/content/src/types';
import { LANGUAGE_NAMES, type MessageKey, useI18n } from './i18n';
import { Link, matchPath, navigate, useLocation } from './router';
import { HomePage } from './pages/HomePage';
import { PeriodicTablePage } from './pages/PeriodicTablePage';
import { LabPage } from './pages/LabPage';
import { MoleculesPage } from './pages/MoleculesPage';
import { NotFoundPage } from './pages/InfoPages';
import { useAuth } from './lib/auth';
import { LoginPage } from './pages/platform/LoginPage';
import { PhetSimPage, SimulationsPage } from './pages/SimulationsPage';
import { TrialBanner, TrialPage } from './pages/platform/TrialPage';
import { LAB_EXPERIMENTS, LAB_TOPICS } from './lib/labExperiments';

// Big pages (question banks, projects, the teacher tools) load only when they are opened, so the first visit is fast.
const PinPage = lazy(() => import('./pages/platform/GuestJoin').then((m) => ({ default: m.PinPage })));
const QuizPage = lazy(() => import('./pages/QuizPage').then((m) => ({ default: m.QuizPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const TeacherPage = lazy(() => import('./pages/platform/TeacherPage').then((m) => ({ default: m.TeacherPage })));
const HostPage = lazy(() => import('./pages/platform/TeacherPages').then((m) => ({ default: m.HostPage })));
const QuizEditorPage = lazy(() => import('./pages/platform/TeacherPages').then((m) => ({ default: m.QuizEditorPage })));
const ResultsPage = lazy(() => import('./pages/platform/TeacherPages').then((m) => ({ default: m.ResultsPage })));
const ClassroomPage = lazy(() => import('./pages/platform/ClassroomPages').then((m) => ({ default: m.ClassroomPage })));
const TaskPage = lazy(() => import('./pages/platform/ClassroomPages').then((m) => ({ default: m.TaskPage })));
const PlayPage = lazy(() => import('./pages/platform/StudentPages').then((m) => ({ default: m.PlayPage })));
const StudentPage = lazy(() => import('./pages/platform/StudentPages').then((m) => ({ default: m.StudentPage })));
const TutorPage = lazy(() => import('./pages/TutorPage').then((m) => ({ default: m.TutorPage })));
const ProjectDetailPage = lazy(() => import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectDetailPage })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })));
const CompetitionsPage = lazy(() => import('./pages/CompetitionsPage').then((m) => ({ default: m.CompetitionsPage })));
const ExamHomePage = lazy(() => import('./pages/platform/ExamPage').then((m) => ({ default: m.ExamHomePage })));
const ExamRunPage = lazy(() => import('./pages/platform/ExamPage').then((m) => ({ default: m.ExamRunPage })));
const IntlPage = lazy(() => import('./pages/platform/IntlPage').then((m) => ({ default: m.IntlPage })));
const WorksheetPage = lazy(() => import('./pages/platform/WorksheetPage').then((m) => ({ default: m.WorksheetPage })));
const LiveRoute = lazy(() => import('./pages/platform/LiveShow').then((m) => ({ default: m.LiveRoute })));
const ShowHostPage = lazy(() => import('./pages/platform/LiveShow').then((m) => ({ default: m.ShowHostPage })));

const NAV: Array<{ to: string; key: MessageKey; match: string; icon: string }> = [
  { to: '/', key: 'nav.home', match: '/', icon: '⌂' },
  { to: '/periodic-table', key: 'nav.periodic', match: '/periodic-table', icon: '▦' },
  { to: '/quiz', key: 'nav.quiz', match: '/quiz', icon: '✓' },
  { to: '/lab/titration', key: 'nav.lab', match: '/lab', icon: '⚗' },
  { to: '/tutor', key: 'nav.tutor', match: '/tutor', icon: '✦' },
  { to: '/simulations', key: 'nav.simulations', match: '/simulations', icon: '▶' },
  { to: '/molecules', key: 'nav.molecules', match: '/molecules', icon: '⬡' },
  { to: '/projects', key: 'nav.projects', match: '/projects', icon: '✎' },
  { to: '/competitions', key: 'nav.competitions', match: '/competitions', icon: '★' },
  { to: '/dashboard', key: 'nav.dashboard', match: '/dashboard', icon: '%' },
  { to: '/student', key: 'nav.student', match: '/student', icon: '◎' },
  { to: '/teacher', key: 'nav.teacher', match: '/teacher', icon: '▤' },
];

// Link to the standalone virtual lab (served as a static page at /chemlab/).
// counts come from the lab's own catalogue (scripts/sync-lab.mjs)
const NEXP = LAB_EXPERIMENTS.length;
const CHEMLAB_LABEL: Record<string, string> = { en: `Chemistry lab (${NEXP} experiments)`, ro: `Laborator de chimie (${NEXP} de experimente)`, tr: `Kimya laboratuvarı (${NEXP} deney)` };
const GAMES_LABEL: Record<string, string> = { en: 'Chemistry games', ro: 'Jocuri de chimie', tr: 'Kimya oyunları' };
const BADGES_LABEL: Record<string, string> = { en: 'My badges', ro: 'Insignele mele', tr: 'Rozetlerim' };
const INTL_LABEL = 'IGCSE & IB';

/** Sends signed-out visitors to the login page and back here afterwards. */
function ToLogin({ next }: { next: string }) {
  useEffect(() => { navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true }); }, [next]);
  return null;
}

function Page({ path }: { path: string }) {
  const { user, ready } = useAuth();
  // Prof. AKAY is for signed-in users only.
  if (path === '/tutor' && !user) return ready ? <ToLogin next="/tutor" /> : null;
  if (path === '/') return <HomePage />;
  if (path === '/periodic-table') return <PeriodicTablePage />;
  if (path === '/quiz') return <QuizPage />;
  if (path === '/lab') return <LabPage />;
  const lab = matchPath('/lab/:sim', path);
  if (lab) return <LabPage sim={lab.sim} />;
  if (path === '/molecules') return <MoleculesPage />;
  if (path === '/simulations') return <SimulationsPage />;
  const phet = matchPath('/simulations/phet/:slug', path);
  if (phet) return <PhetSimPage slug={phet.slug} />;
  if (path === '/competitions') return <CompetitionsPage />;
  if (path === '/projects') return <ProjectsPage />;
  const proj = matchPath('/projects/:id', path);
  if (proj) return <ProjectDetailPage id={proj.id} />;
  if (path === '/dashboard') return <DashboardPage />;
  if (path === '/tutor') return <TutorPage />;
  if (path === '/teacher') return <TeacherPage />;
  if (path === '/login') return <LoginPage />;
  // /pin: short address for the projector — the student page with the PIN box focused
  if (path === '/student') return <StudentPage />;
  if (path === '/pin') return <PinPage />;
  if (path === '/teacher/quizzes/new') return <QuizEditorPage id={null} />;
  if (path === '/teacher/worksheet') return <WorksheetPage />;
  if (path === '/exam') return <ExamHomePage />;
  if (path === '/intl') return <IntlPage />;
  if (path === '/try') return <TrialPage />;
  const m = (p: string) => matchPath(p, path);
  const cls = m('/teacher/classes/:id') || m('/classes/:id'); if (cls) return <ClassroomPage id={cls.id} />;
  const task = m('/tasks/:id'); if (task) return <TaskPage id={task.id} />;
  const qz = m('/teacher/quizzes/:id'); if (qz) return <QuizEditorPage id={qz.id} />;
  const rs = m('/teacher/assignments/:id'); if (rs) return <ResultsPage id={rs.id} />;
  const sh = m('/teacher/show/:id'); if (sh) return <ShowHostPage id={sh.id} />;
  const lv = m('/teacher/live/:id'); if (lv) return <LiveRoute id={lv.id} fallback={<HostPage id={lv.id} />} />;
  const pl = m('/play/:id'); if (pl) return <PlayPage id={pl.id} />;
  const ex = m('/exam/:id'); if (ex) return <ExamRunPage id={ex.id} />;
  return <NotFoundPage />;
}

function useOnline() {
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  return online;
}

function AccountButton() {
  const { t } = useI18n();
  const { user } = useAuth();
  if (!user) return <Link to="/login" className="btn btn-small account-btn">{t('nav.login')}</Link>;
  return (
    <Link to={user.role === 'teacher' ? '/teacher' : '/student'} className="account-btn account-chip" title={t('auth.signedInAs', { n: user.displayName })}>
      <span className="avatar-initial" aria-hidden="true">{user.displayName.trim().charAt(0).toUpperCase()}</span>
      <span className="account-name">{user.displayName}</span>
    </Link>
  );
}

export function App() {
  const { t, lang, setLang } = useI18n();
  const { path } = useLocation();
  const online = useOnline();
  const [menuOpen, setMenuOpen] = useState(false);
  const active = (m: string) => (m === '/' ? path === '/' : path === m || path.startsWith(`${m}/`));

  useEffect(() => { setMenuOpen(false); }, [path]);
  // Tell plain scripts (feedback button, screen readers) the current site language.
  useEffect(() => { document.documentElement.lang = lang; document.documentElement.setAttribute('data-chemai-lang', lang); }, [lang]);
  // Installable app: manifest, icons and the offline service worker (shared loader also used by the static lab and games).
  useEffect(() => {
    if (document.querySelector('script[data-pwa]')) return;
    const sc = document.createElement('script'); sc.src = '/pwa.js'; sc.defer = true; sc.dataset.pwa = '1'; document.head.appendChild(sc);
  }, []);
  const { user } = useAuth();
  // Phone tab bar: core tools plus one account-aware entry (My classes / Teacher / Log in).
  const pick = (to: string) => NAV.find((n) => n.to === to)!;
  const tabItems = [
    pick('/'),
    pick('/lab/titration'),
    user ? pick('/tutor') : pick('/molecules'),
    user?.role === 'student' ? pick('/student') : user?.role === 'teacher' ? pick('/teacher') : pick('/quiz'),
    user ? pick('/quiz') : { to: '/login', key: 'nav.login' as MessageKey, match: '/login', icon: '◎' },
  ];

  return (
    <div className="shell">
      <a href="#main" className="skip-link">{t('app.skip')}</a>
      <header className="topbar">
        <Link to="/" className="brand" aria-label={`${t('app.name')} ${t('app.byline')}`}>
          <span className="brand-mark" aria-hidden="true">Ch</span>
          <span className="brand-text"><span className="brand-name">{t('app.name')}</span><span className="brand-by">{t('app.byline')}</span></span>
        </Link>
        <div className="topbar-right">
          <AccountButton />
          <label className="sr-only" htmlFor="lang">{t('app.language')}</label>
          <select id="lang" className="lang-select" value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
            {LANGS.map((l) => <option key={l} value={l}>{LANGUAGE_NAMES[l]}</option>)}
          </select>
          <button type="button" className="menu-btn" aria-expanded={menuOpen} aria-controls="mainnav" onClick={() => setMenuOpen((o) => !o)}>
            <span aria-hidden="true">☰</span> <span className="sr-only">{t('app.menu')}</span>
          </button>
        </div>
      </header>

      <div className="body">
        <nav id="mainnav" className={menuOpen ? 'sidenav open' : 'sidenav'} aria-label={t('app.menu')}>
          <ul>
            {NAV.filter((n) => n.to !== '/tutor' || user).map((n) => (
              <Fragment key={n.to}>
                <li>
                  <Link to={n.to} className={active(n.match) ? 'navlink on' : 'navlink'} aria-current={active(n.match) ? 'page' : undefined}>
                    <span className="navicon" aria-hidden="true">{n.icon}</span>{t(n.key)}
                  </Link>
                </li>
                {n.to === '/quiz' && (
                  <li>
                    <Link to="/intl" className={active('/intl') ? 'navlink on' : 'navlink'} aria-current={active('/intl') ? 'page' : undefined}>
                      <span className="navicon" aria-hidden="true">🌍</span>{INTL_LABEL}
                    </Link>
                  </li>
                )}
                {n.to === '/lab/titration' && (
                  <li>
                    <a href={`/chemlab/?lang=${lang}`} className="navlink">
                      <span className="navicon" aria-hidden="true">🧪</span>{CHEMLAB_LABEL[lang] ?? CHEMLAB_LABEL.en}
                    </a>
                  </li>
                )}
                {n.to === '/lab/titration' && (
                  <li>
                    <a href={`/games/?lang=${lang}`} className="navlink">
                      <span className="navicon" aria-hidden="true">🎮</span>{GAMES_LABEL[lang] ?? GAMES_LABEL.en}
                    </a>
                  </li>
                )}
                {n.to === '/lab/titration' && (
                  <li>
                    <a href={`/progress/?lang=${lang}`} className="navlink">
                      <span className="navicon" aria-hidden="true">🏅</span>{BADGES_LABEL[lang] ?? BADGES_LABEL.en}
                    </a>
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        </nav>
        <main id="main" tabIndex={-1}>
          <TrialBanner />
          {!online && <p className="offline" role="status">{t('app.offline')}</p>}
          <Suspense fallback={<p className="muted" style={{ padding: 24 }} aria-live="polite">…</p>}><Page path={path} /></Suspense>
        </main>
      </div>

      <nav className="tabbar" aria-label={t('app.menu')}>
        {tabItems.map((n) => (
          <Link key={n.to} to={n.to} className={active(n.match) ? 'tabbar-item on' : 'tabbar-item'} aria-current={active(n.match) ? 'page' : undefined}>
            <span aria-hidden="true" className="tabbar-icon">{n.icon}</span>
            <span className="tabbar-label">{t(n.key)}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}
